import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import { useMount } from '@uifabric/portal-ux/lib/hooks/useMount';
import { gridUnit } from '@uifabric/portal-ux/lib/styling/stylingConstants';
import { TitleBar, TitleBarLabel } from '@uifabric/portal-ux/lib/TitleBar';
import { Link } from '@fluentui/react/lib/Link';
import { ResponsiveMode } from '@fluentui/react/lib/utilities/decorators/withResponsiveMode';
import * as React from 'react';
import { FontWeights } from '@fluentui/react';
import { useDispatch } from 'react-redux';
import { AccountActions } from '../models';
import { useLeaveOrgFlow } from '../LeaveOrganization/hooks/useLeaveOrgFlow';
import { useAuth } from '@iamexperiences/feature-auth';

export enum LeaveOrgQueryParam {
  tenantIdToLeave = 'leave',
  autoLoginTenant = 'tenant',
  tenantIdToRedirectBackTo = 'redirectToTenant'
}

export interface IOrganizationCardProps {
  homeTenantId: string | undefined;
  name: string;
  isCurrent: boolean;
  isHome: boolean;
  tenantId: string;
}

export const OrganizationCard: React.FunctionComponent<IOrganizationCardProps> = ({
  homeTenantId,
  isCurrent,
  isHome,
  name,
  tenantId
}) => {
  const [t] = useLocalization('Account');
  const { auth } = useAuth();
  const dispatch = useDispatch();
  useLeaveOrgFlow(name, isCurrent);

  const onClick = React.useCallback(() => {
    if (!isCurrent) {
      const url = new URL(location.href);
      url.searchParams.set(LeaveOrgQueryParam.autoLoginTenant, tenantId);
      url.searchParams.set(LeaveOrgQueryParam.tenantIdToRedirectBackTo, homeTenantId || '');
      url.searchParams.set(LeaveOrgQueryParam.tenantIdToLeave, tenantId);
      window.location.href = url.href;
    } else {
      dispatch({ type: AccountActions.showLeaveOrgDialog });
    }
  }, [isCurrent, homeTenantId, tenantId, dispatch]);

  useMount(() => {
    const url = new URL(location.href);
    const tenantIdToLeave = url.searchParams.get(LeaveOrgQueryParam.tenantIdToLeave);

    if (tenantId === tenantIdToLeave) {
      if (!isCurrent) {
        auth.login({ tenantId: tenantIdToLeave! });
      } else {
        dispatch({ type: AccountActions.showLeaveOrgDialog });
      }
    }
  });

  return (
    <TitleBar
      showBorder
      responsiveMode={ResponsiveMode.medium}
      iconProps={{ iconName: isHome ? 'Home' : 'Work' }}
      styles={{
        subComponentStyles: {
          control: { root: { margin: `${gridUnit / 2}px 0` } }
        }
      }}
    >
      <TitleBarLabel
        width={'40%'}
        styles={{
          root: {
            fontWeight: FontWeights.semibold,
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }
        }}
      >
        {name}
      </TitleBarLabel>
      <TitleBarLabel width={'20%'} responsiveVisibilityMin={ResponsiveMode.large}>
        {isCurrent && t('signedIn')}
      </TitleBarLabel>
      <TitleBarLabel width={'40%'}>
        {!isHome ? <Link onClick={onClick}>{t('leaveOrganization')}</Link> : t('cannotLeaveOrg')}
      </TitleBarLabel>
    </TitleBar>
  );
};
